import {attr, controller, target, targets} from '@github/catalyst'
import {ActionsCalculatorElement} from './marketing/pricing-calculator/actions'
import {CodespacesCalculatorElement} from './marketing/pricing-calculator/codespaces'
import {GhasCalculatorElement} from './marketing/pricing-calculator/ghas'
import {GhecCalculatorElement} from './marketing/pricing-calculator/ghec'
import {PackagesCalculatorElement} from './marketing/pricing-calculator/packages'
import {LfsCalculatorElement} from './marketing/pricing-calculator/lfs'
import {TeamsCalculatorElement} from './marketing/pricing-calculator/teams'
import {CopilotCalculatorElement} from './marketing/pricing-calculator/copilot'
import {ProCalculatorElement} from './marketing/pricing-calculator/pro'
import {CodeQualityCalculatorElement} from './marketing/pricing-calculator/code-quality'
import {currencyFormatter} from './marketing/pricing-calculator/utils'
import {loaded} from '@github-ui/document-ready'

type SectionCalculator =
  | ActionsCalculatorElement
  | GhasCalculatorElement
  | CodespacesCalculatorElement
  | PackagesCalculatorElement
  | LfsCalculatorElement
  | GhecCalculatorElement
  | TeamsCalculatorElement
  | CopilotCalculatorElement
  | ProCalculatorElement
  | CodeQualityCalculatorElement

@controller('pricing-calculator')
class PricingCalculatorElement extends HTMLElement {
  @targets declare calculatorSections: SectionCalculator[]

  @target declare totalCostPerMonthDisplay: HTMLElement
  @target declare codespacesTotal: HTMLElement
  @target declare codespacesTotalContainer: HTMLElement
  @target declare ghasTotal: HTMLElement
  @target declare ghasTotalContainer: HTMLElement
  @target declare actionsTotal: HTMLElement
  @target declare actionsTotalContainer: HTMLElement
  @target declare packagesTotal: HTMLElement
  @target declare packagesTotalContainer: HTMLElement
  @target declare lfsTotal: HTMLElement
  @target declare lfsTotalContainer: HTMLElement
  @target declare ghecTotalContainer: HTMLElement
  @target declare teamsTotalContainer: HTMLElement
  @target declare copilotTotal: HTMLElement
  @target declare copilotTotalContainer: HTMLElement
  @target declare proTotal: HTMLElement
  @target declare proTotalContainer: HTMLElement
  @target declare codeQualityTotal: HTMLElement
  @target declare codeQualityTotalContainer: HTMLElement
  @target declare subtotalsOverview: HTMLElement
  @target declare estimateTotalContainer: HTMLElement
  @target declare estimateDetails: HTMLElement
  get estimateDetailsOutOfView() {
    return this.estimateDetails.getBoundingClientRect().top >= document.documentElement.clientHeight - 1
  }

  static attrPrefix = ''
  @attr declare totalCostPerMonth: number

  setEstimateDetailsContainerBottom = async () => {
    await loaded
    const calculatorEl = document.querySelector('pricing-calculator') as PricingCalculatorElement
    calculatorEl.estimateTotalContainer.style.bottom = `-${
      calculatorEl.estimateDetails.getBoundingClientRect().height
    }px`
  }

  connectedCallback() {
    document.addEventListener('subtotalChanged', this.updateEstimateTotals.bind(this) as (e: Event) => void)
    document.addEventListener('formDataUpdated', this.toggleResetButton.bind(this) as (e: Event) => void)

    new ActionsCalculatorElement()
    new GhasCalculatorElement()
    new CodespacesCalculatorElement()
    new PackagesCalculatorElement()
    new LfsCalculatorElement()
    new GhecCalculatorElement()
    new TeamsCalculatorElement()
    new CopilotCalculatorElement()
    new ProCalculatorElement()
    new CodeQualityCalculatorElement()

    this.setEstimateDetailsContainerBottom()

    this.estimateTotalContainer.onmouseenter = () => {
      if (this.estimateDetailsOutOfView) {
        this.estimateTotalContainer.style.transform = `translateY(-${
          this.estimateDetails.getBoundingClientRect().height
        }px)`
      }
    }

    this.estimateTotalContainer.onmouseleave = () => {
      this.estimateTotalContainer.style.transform = 'none'
    }
  }

  toggleResetButton(event: CustomEvent) {
    const {sectionName, resetEnabled} = event.detail
    const resetButton = this.querySelector(`[data-target="pricing-calculator.${sectionName}CalculatorReset"]`)

    if (resetEnabled) {
      resetButton?.classList.remove('faded-out')
    } else {
      resetButton?.classList.add('faded-out')
    }
  }

  updateEstimateTotals(event: CustomEvent) {
    this.totalCostPerMonth = this.calculatorSections.reduce((accum, section) => {
      return section.active ? accum + section.costPerMonth : accum
    }, 0)

    const {sectionName, amount, active} = event.detail
    const sectionTotalContainer = this.querySelector<HTMLElement>(
      `[data-target="pricing-calculator.${sectionName}Container"]`,
    )
    const sectionTotal = this.querySelector<HTMLElement>(`[data-target="pricing-calculator.${sectionName}"]`)

    if (!sectionTotalContainer || !sectionTotal) {
      return
    }

    if (active) {
      sectionTotal.textContent = currencyFormatter.format(amount / 100)
      sectionTotalContainer.hidden = false
    } else {
      sectionTotalContainer.hidden = true
    }
  }

  getSummaryForCSV() {
    const overview = [
      'Total Estimated Monthly Cost',
      '',
      '',
      `"${currencyFormatter.format(this.totalCostPerMonth / 100)}"`,
    ].join(',')

    const detailsHeader = ['Service', 'Configuration Summary', 'Notes', 'Estimated Monthly Cost Per Service'].join(',')

    const serviceDetails = this.calculatorSections
      .filter(section => section.active)
      .map(section => section.getSummaryForCSV())
      .flat()

    return [
      `data:text/csv;charset=utf-8,${detailsHeader}`,
      serviceDetails.map(row => row.join(',')).join('\n'),
      `\n${overview}`,
    ].join('\n')
  }

  downloadCSV() {
    const csvContent = this.getSummaryForCSV()
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `estimate-${new Date().toLocaleDateString()}`)
    document.body.appendChild(link) // Required for FF
    link.click()
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'total-cost-per-month') {
      this.totalCostPerMonthDisplay.textContent = currencyFormatter.format(this.totalCostPerMonth / 100)
    }
  }
}

@controller('pricing-calculator-section')
class PricingCalculatorSectionElement extends HTMLElement {
  @target declare sectionInputs: HTMLElement
  @target declare sectionContent: HTMLElement
  @target declare sectionVisibleToggleContainer: HTMLElement
  @target declare sectionVisibleToggle: HTMLElement
  @target declare moreInfoLink: HTMLElement
  @target declare icon: HTMLElement

  get pricingCalculator(): PricingCalculatorElement | null {
    return this.closest('pricing-calculator')
  }

  toggleSubsectionVisible() {
    this.sectionInputs.classList.toggle('calculator-section--hidden')
    this.sectionVisibleToggle.classList.toggle('calculator-chevron--expanded')
  }

  connectedCallback() {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('feature')) {
      return
    }
    const feature = params.get('feature')
    /* eslint-disable-next-line wc/no-child-traversal-in-connectedcallback */
    const calculator = this.firstElementChild as SectionCalculator

    if (
      (feature === 'codespaces' && calculator instanceof CodespacesCalculatorElement) ||
      (feature === 'actions' && calculator instanceof ActionsCalculatorElement) ||
      (feature === 'packages' && calculator instanceof PackagesCalculatorElement) ||
      (feature === 'lfs' && calculator instanceof LfsCalculatorElement)
    ) {
      /* eslint-disable-next-line wc/no-child-traversal-in-connectedcallback, @typescript-eslint/no-non-null-assertion */
      this.querySelector<HTMLElement>('input')!.click()

      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const section = document.getElementById(feature)!
      section.scrollIntoView()
    }
  }

  async toggleSubsectionActive() {
    this.sectionContent.classList.toggle('calculator-section--hidden')
    this.moreInfoLink.classList.toggle('calculator-more-info--hidden')
    this.sectionVisibleToggleContainer.classList.toggle('calculator-toggle-container--hidden')

    const calculator = this.firstElementChild as SectionCalculator
    calculator.active = !calculator.active

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const estimateDetailsHeight = this.pricingCalculator!.estimateDetails.getBoundingClientRect().height
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    this.pricingCalculator!.estimateTotalContainer.style.bottom = `-${estimateDetailsHeight}px`
  }
}
